import { useContext, useEffect, useState } from "react";
import { useTranslation } from "@chr/online-shared-ui-localizations";
import { Divider, Stack, useMediaQuery } from "@mui/material";
import { SideDrawer } from "@/common/components/side-drawer/SideDrawer";
import { Banner, Button, TextArea, Typography } from "@chr/eds-react";
import { MultipleEmailInput } from "@/common/components/multiple-email-input/MultipleEmailInput";
import { useEmailShareTracking } from "@/common/apis/notifications-mutation";
import { ShareTrackingSuccess } from "./ShareTrackingSuccess";
import { ProgressButton } from "@/common/components/progress-button/ProgressButton";
import { CopyToClipboard } from "@/common/components/copy-to-clipboard/CopyToClipboard";
import { ConfigContext } from "@chr/navisphereonline-shared-components";
import customTheme from "@/common/components/theme-provider/customTheme";

export interface ShareTrackingModalProps {
  open: boolean;
  handleClose: (event: object) => void
  trackingNumber: string;
}

// Reusable Section component
const SummarySection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Stack spacing={1}>
    <Typography variant="subtitle200">{title}</Typography>
    {children}
  </Stack>
);

export const ShareTrackingModal = ({open, handleClose, trackingNumber}: ShareTrackingModalProps) => {
  const translate = useTranslation();
  const theme = customTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const config = useContext(ConfigContext) as AppConfig;

  const { isLoading, isSuccess, isError, mutate:sendEmail, reset } = useEmailShareTracking();
  const [emailToValues, setEmailToValues] = useState<string[]>([]);
  const [messageValue, setMessageValue] = useState('');
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const trackingUrl = `${config?.baseUrl}/tracking/#/?trackingNumber=${trackingNumber}`

  useEffect(()=>{
    if(isError){
      setShowErrorBanner(true);
    }
  }, [isError, setShowErrorBanner]);

  const resetForm = () => {
    setEmailToValues([]);
    setMessageValue('');
    setShowErrorBanner(false);
    reset();
  }

  return (
    <SideDrawer
      open={open}
      handleClose={handleClose}
      title={translate('Share External Tracking')}
    >
      { isSuccess
        ? <ShareTrackingSuccess {...{emailToValues, messageValue, trackingNumber, onAgain:resetForm}}/>
        : <Stack marginTop={4} spacing={4} >
          {showErrorBanner &&
            <Banner
              onClose={()=>{setShowErrorBanner(false)}}
              title={translate('Email could not be sent.')}
              description={translate('Please try again later.')}
              severity="negative"
            />}
          <SummarySection title={translate("Send Email")}>
            <Typography variant="body100">
              {translate(
                "Share access to the external tracking page for this booking. The email will come from Navisphere2.0@chrobinson.com"
              )}
            </Typography>
          </SummarySection>
          <MultipleEmailInput
            label={translate('Email To')}
            emails={emailToValues}
            onChange={setEmailToValues}
            fullWidth
          />
          <TextArea
            fullWidth
            label={translate('Message')}
            maxLength={800}
            helperText={messageValue.length >= 800
              ? translate('Maximum of 800 characters reached.')
              : undefined}
            value={messageValue}
            onChange={(e) => {
              setMessageValue(e.target.value);
            }}
          />
          <Stack
            data-testid="ButtonStack"
            direction={isMobileScreen ?'column-reverse' :'row' }
            spacing={2}
            justifyContent="space-between"
          >
            <Button
              buttonText={ translate('Cancel')}
              disabled={isLoading}
              onClick={handleClose}
              fullWidth
              variant="secondary"
            />
            <ProgressButton
              buttonText={translate('Send')}
              showProgress={isLoading}
              disabled={Boolean(emailToValues.length < 1)}
              onClick={() => sendEmail({emailTos:emailToValues, message:messageValue, trackingNumber})}
              fullWidth
              variant="primary"
            />
          </Stack>
        </Stack>
      }
      <Stack marginTop={4} spacing={4}>
        <Divider />
        <SummarySection title={translate("Copy Link")}>
          <Typography variant="body100">
            {translate(
              "Copy a direct link to this booking’s external tracking page to your clipboard."
            )}
          </Typography>
          <CopyToClipboard text={trackingUrl} />
        </SummarySection>
        <Typography variant="body200" color={theme.palette.grey[700]}>
          {translate('Your use of shipment tracking information, including the disclosure of tracking information to third parties, is at your discretion and sole risk. Shipment tracking information is provided “as is” and C.H. Robinson disclaims all warranties or conditions, expressed or implied.')}
        </Typography>
      </Stack>
    </SideDrawer>
  );
}
